import { useState, type ComponentType, type ReactNode, type SVGProps } from 'react';
import { useNavigate } from 'react-router-dom';

import { PictoFormulaireEnLigne, PictoSignatureElectronique, PictoDeclarationSinistre, PictoServiceClient, PictoCall } from '@/assets/icons-april-fr/pictos';
import { useContractsData } from '@/hooks/api/use-contracts';
import { useCurrentUser } from '@/hooks/api/use-user';
import { pushEvent } from '@/lib/analytics';
import { hasOnlyContractApporteur43397 } from '@/lib/contracts';

import ContractPickerModal from '@/components/shared/contract-picker-modal';
import ProductPickerModal from '@/components/shared/product-picker-modal';

interface QuickLink {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  action: string | (() => void);
  itemName: string;
  color: string;
  bg: string;
  opensDialog?: boolean;
}

const AccesRapides = () => {
  const contracts = useContractsData();
  const prefilled_subscribe_links: Record<string, string> = useCurrentUser().prefilled_subscribe_links || {};
  const hasOnlyApporteur43397 = hasOnlyContractApporteur43397(contracts);
  const navigate = useNavigate();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [productPickerOpen, setProductPickerOpen] = useState(false);

  const handleQuickLinkClick = (itemName: string, action: QuickLink['action']) => {
    pushEvent('navRapide', {
      navRapide: {
        item: itemName,
      },
    });

    if (typeof action === 'function') {
      action();
    } else if (typeof action === 'string') {
      navigate(action);
    }
  };

  const chooseContract = () => {
    if (contracts.length === 1) {
      navigate(`/mes-contrats/mon-contrat/${contracts[0].id}`);
      return;
    }
    setPickerOpen(true);
  };

  const labelOverrides: Record<string, { label: ReactNode; article: string }> = {
    moto: { label: 'moto', article: 'une' },
    '50': {
      label: (
        <>
          cyclo <span aria-hidden="true">50cm3</span>
          <span className="sr-only">50 centimètres cubes</span>
        </>
      ),
      article: 'un',
    },
    scooter: { label: 'scooter', article: 'un' },
    quad: { label: 'quad', article: 'un' },
    verte: { label: 'moto verte', article: 'une' },
    trottinette: { label: 'trottinette électrique', article: 'une' },
    velo: { label: 'vélo à assistance électrique', article: 'un' },
    livraison: { label: 'véhicule de livraison', article: 'un' },
  };

  const productOptions = prefilled_subscribe_links
    ? Object.keys(prefilled_subscribe_links)
        .filter((key) => key !== 'general')
        .reduce<Record<string, ReactNode>>((acc, key) => {
          const override = labelOverrides[key];
          if (override) {
            acc[key] = (
              <>
                Je souhaite assurer {override.article} {override.label}
              </>
            );
          }
          return acc;
        }, {})
    : {};

  const chooseProduct = () => {
    setProductPickerOpen(true);
  };

  const handleProductSelect = (key: string) => {
    const link = prefilled_subscribe_links[key];
    if (link) {
      window.open(link, '_blank');
    }
  };

  const quickLinks: QuickLink[] = [
    ...(hasOnlyApporteur43397 ? [{
      icon: PictoFormulaireEnLigne,
      label: 'Nouveau devis',
      action: chooseProduct,
      itemName: 'Réaliser un nouveau devis',
      color: 'text-primary',
      bg: 'bg-primary/10',
      opensDialog: true,
    }] : []),
    {
      icon: PictoSignatureElectronique,
      label: 'Modifier contrat',
      action: chooseContract,
      itemName: 'Modifier mon contrat',
      color: 'text-accent',
      bg: 'bg-accent/10',
      opensDialog: contracts.length > 1,
    },
    {
      icon: PictoDeclarationSinistre,
      label: 'Déclarer sinistre',
      action: '/mes-sinistres/je-declare-un-sinistre',
      itemName: 'Déclarer un sinistre',
      color: 'text-destructive',
      bg: 'bg-destructive/10',
    },
    {
      icon: PictoServiceClient,
      label: "Obtenir de l'aide",
      action: '/contact',
      itemName: "Obtenir de l'aide",
      color: 'text-success',
      bg: 'bg-success/10',
    },
    {
      icon: PictoCall,
      label: 'Assistance',
      action: '/assistance',
      itemName: 'Assistance panne, accident',
      color: 'text-warning',
      bg: 'bg-warning/10',
    },
  ];

  return (
    <>
      <nav aria-label="Accès rapides" data-tutorial="acces-rapides" className="grid grid-cols-3 md:grid-cols-5 gap-3 md:gap-4 mb-6">
        {quickLinks.map((link, index) => {
          const Icon = link.icon;
          return (
            <button
              key={index}
              type="button"
              aria-label={link.itemName}
              {...(link.opensDialog ? { 'aria-haspopup': 'dialog', 'aria-expanded': link.action === chooseProduct ? productPickerOpen : pickerOpen } : {})}
              className="group flex flex-col items-center gap-2 p-4 rounded-xl border bg-card cursor-pointer transition-all duration-200 ease-out hover:shadow-lg hover:shadow-primary/10 hover:border-primary/30 hover:scale-[1.03] active:scale-100"
              onClick={() => handleQuickLinkClick(link.itemName, link.action)}
            >
              <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-lg transition-transform duration-200 group-hover:scale-110">
                <Icon className="w-5 h-5 md:w-6 md:h-6" aria-hidden="true" focusable="false" />
              </div>
              <span className="text-sm font-medium text-foreground leading-tight text-center">
                {link.label}
              </span>
            </button>
          );
        })}
      </nav>

      <ContractPickerModal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(c) => navigate(`/mes-contrats/mon-contrat/${c.id}`)}
        contracts={contracts}
      />

      <ProductPickerModal
        isOpen={productPickerOpen}
        onClose={() => setProductPickerOpen(false)}
        onSelect={handleProductSelect}
        options={productOptions}
      />
    </>
  );
};

export default AccesRapides;
